import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  Save, 
  RotateCcw, 
  Code, 
  Eye, 
  CheckCircle,
  AlertCircle,
  Loader2,
  Palette,
  Copy,
  Download,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// CSS Templates
const CSS_TEMPLATES = {
  default: `/* CSS Personalizado SGFI Capital */
/* Los cambios se aplican inmediatamente en toda la aplicación */

/* Ejemplo: Cambiar colores principales */
/*
:root {
  --primary-color: #3B82F6;
  --background-dark: #1D262D;
  --card-background: #252F38;
}
*/

/* Ejemplo: Personalizar botones */
/*
button.bg-blue-500 {
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
}
*/

/* Ejemplo: Cambiar fuentes */
/*
body {
  font-family: 'Inter', sans-serif;
}
*/
`,
  darkMode: `/* Tema Oscuro Personalizado */
.bg-\\[\\#1D262D\\] {
  background-color: #0f1419 !important;
}

.bg-\\[\\#252F38\\] {
  background-color: #1a2332 !important;
}

/* Cards más oscuros */
.border-white\\/10 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
`,
  blueAccent: `/* Tema Azul Intenso */
.text-blue-400 {
  color: #60A5FA !important;
}

.bg-blue-500 {
  background-color: #3B82F6 !important;
}

.bg-blue-500:hover {
  background-color: #2563EB !important;
}

/* Gradientes azules */
.from-blue-500 {
  --tw-gradient-from: #3B82F6 !important;
}
`,
  greenAccent: `/* Tema Verde Esmeralda */
.text-emerald-400 {
  color: #34D399 !important;
}

.bg-emerald-500 {
  background-color: #10B981 !important;
}

/* Balance en verde */
.text-white.font-bold {
  color: #10B981 !important;
}
`
};

export default function CSSEditor() {
  const [css, setCss] = useState('');
  const [originalCss, setOriginalCss] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);

  // Cargar CSS actual
  useEffect(() => {
    fetchCSS();
  }, []);

  // Detectar cambios
  useEffect(() => {
    setHasChanges(css !== originalCss);
  }, [css, originalCss]);

  // Aplicar preview en tiempo real
  useEffect(() => {
    if (previewMode) {
      applyPreviewCSS(css);
    } else {
      applyPreviewCSS(originalCss);
    }
  }, [css, previewMode, originalCss]);

  const fetchCSS = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/css`);
      const loadedCss = response.data.css || CSS_TEMPLATES.default;
      setCss(loadedCss);
      setOriginalCss(loadedCss);
    } catch (error) {
      // Error silenced: 'Error loading CSS:', error);
      setCss(CSS_TEMPLATES.default);
      setOriginalCss(CSS_TEMPLATES.default);
    } finally {
      setLoading(false);
    }
  };

  const applyPreviewCSS = useCallback((cssText) => {
    // Remover estilo anterior
    const existingStyle = document.getElementById('custom-css-preview');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Aplicar nuevo estilo
    if (cssText) {
      const style = document.createElement('style');
      style.id = 'custom-css-preview';
      style.textContent = cssText;
      document.head.appendChild(style);
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings/css`, { css });
      setOriginalCss(css);
      toast.success('CSS guardado correctamente');
    } catch (error) {
      // Error silenced: 'Error saving CSS:', error);
      toast.error('Error al guardar CSS');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setCss(originalCss);
    toast.info('Cambios descartados');
  };

  const handleLoadTemplate = (templateKey) => {
    const template = CSS_TEMPLATES[templateKey];
    setCss(prev => prev + '\n\n' + template);
    toast.success('Plantilla añadida');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(css);
    toast.success('CSS copiado al portapapeles');
  };

  const handleDownload = () => {
    const blob = new Blob([css], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sgfi-custom-styles.css';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSS descargado');
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCss(event.target.result);
        toast.success('CSS cargado desde archivo');
      };
      reader.readAsText(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="css-editor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Code className="w-7 h-7 text-blue-400" />
            Editor de CSS
          </h1>
          <p className="text-slate-400 mt-1">
            Personaliza los estilos de toda la plataforma
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-amber-400 text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Cambios sin guardar
            </span>
          )}
          
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges}
            className="border-white/10 text-slate-300 hover:bg-white/5"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Descartar
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-blue-500 hover:bg-blue-600"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-[#252F38] border border-white/10 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Preview Toggle */}
          <Button
            variant={previewMode ? "default" : "outline"}
            size="sm"
            onClick={() => setPreviewMode(!previewMode)}
            className={previewMode ? "bg-emerald-500 hover:bg-emerald-600" : "border-white/10 text-slate-300"}
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? 'Preview ON' : 'Preview OFF'}
          </Button>
          
          <div className="w-px h-6 bg-white/10" />
          
          {/* Templates */}
          <span className="text-slate-400 text-sm">Plantillas:</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleLoadTemplate('darkMode')}
            className="border-white/10 text-slate-300 hover:bg-white/5"
          >
            <Palette className="w-4 h-4 mr-1" />
            Dark
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleLoadTemplate('blueAccent')}
            className="border-white/10 text-slate-300 hover:bg-white/5"
          >
            <Palette className="w-4 h-4 mr-1 text-blue-400" />
            Azul
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleLoadTemplate('greenAccent')}
            className="border-white/10 text-slate-300 hover:bg-white/5"
          >
            <Palette className="w-4 h-4 mr-1 text-emerald-400" />
            Verde
          </Button>
          
          <div className="w-px h-6 bg-white/10" />
          
          {/* Actions */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="border-white/10 text-slate-300 hover:bg-white/5"
          >
            <Copy className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="border-white/10 text-slate-300 hover:bg-white/5"
          >
            <Download className="w-4 h-4" />
          </Button>
          <label className="cursor-pointer">
            <Button
              variant="outline"
              size="sm"
              className="border-white/10 text-slate-300 hover:bg-white/5 pointer-events-none"
              asChild
            >
              <span>
                <Upload className="w-4 h-4" />
              </span>
            </Button>
            <input
              type="file"
              accept=".css"
              onChange={handleUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Editor */}
      <div className="bg-[#252F38] border border-white/10 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5">
          <div className="flex items-center gap-2">
            <Code className="w-4 h-4 text-blue-400" />
            <span className="text-white font-medium">custom-styles.css</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>{css.length} caracteres</span>
            <span>•</span>
            <span>{css.split('\n').length} líneas</span>
          </div>
        </div>
        
        <textarea
          value={css}
          onChange={(e) => setCss(e.target.value)}
          className="w-full h-[500px] bg-[#1D262D] text-slate-100 font-mono text-sm p-4 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          placeholder="/* Escribe tu CSS personalizado aquí */"
          spellCheck={false}
          data-testid="css-editor-textarea"
        />
      </div>

      {/* Help Section */}
      <div className="bg-[#252F38] border border-white/10 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-blue-400" />
          Guía Rápida
        </h3>
        <div className="grid md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="text-slate-300 font-medium mb-2">Clases comunes:</h4>
            <ul className="text-slate-400 space-y-1">
              <li><code className="text-blue-400">.bg-[#1D262D]</code> - Fondo principal</li>
              <li><code className="text-blue-400">.bg-[#252F38]</code> - Fondo de cards</li>
              <li><code className="text-blue-400">.text-white</code> - Texto blanco</li>
              <li><code className="text-blue-400">.text-blue-400</code> - Texto azul</li>
              <li><code className="text-blue-400">.text-emerald-400</code> - Texto verde</li>
            </ul>
          </div>
          <div>
            <h4 className="text-slate-300 font-medium mb-2">Tips:</h4>
            <ul className="text-slate-400 space-y-1">
              <li>• Usa <code className="text-amber-400">!important</code> para sobrescribir estilos</li>
              <li>• El preview se aplica en tiempo real</li>
              <li>• Guarda para que los cambios persistan</li>
              <li>• Puedes descargar/subir archivos CSS</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
